<template>
	<div :class="{ 'show-info-toggle': true }">
		<h2>Matches area visibility</h2>

		<div>
			<p v-if="showInfoForceUntil" class="disabled">
				Other brands can see your brand name and logo in the Matches Area, you can change your visibility on {{ showInfoForceUntil }}.
			</p>
			<p v-if="subscription && !subscription.active" class="disabled">Changing your visibility is only available on paid subscriptions.</p>
			<Switcher switcherLabel="Hidden" switcherLabel2="Visible" sameColour="1" v-model="showInfo" class="switcher" :flipped="true" />
			<p>
				{{ p }}<Tip position="fromRight" :class="{ theTip: true }">{{ tip }}</Tip>
			</p>
		</div>
	</div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { useVueInstance, useDialogs } from "../composables/useVueInstance";
import ApiService from "./../services/api.service";
import Switcher from "./Switcher.vue";
import Tip from "./HelpTip.vue";

export default {
	name: "ToggleShowBrand",
	components: {
		Switcher,
		Tip,
	},
	props: {
		show_info: {
			type: Boolean,
			default: false,
		},
		brandId: {
			type: Number,
			default: 0,
		},
	},
	emits: ["toggle-show-info", "success"],
	setup(props, { emit }) {
		const { store, $toast } = useVueInstance();
		const { confirm } = useDialogs();

		// --- State ---
		const showInfo = ref(false);
		const noWatch = ref(false);

		// --- Vuex State/Actions ---
		const loadDashboard = () => store.dispatch("general/loadDashboard");
		const userBrands = computed(() => store.state.general.userBrands);
		const showInfoForceUntil = computed(() => userBrands.value.find((brand) => brand.id === props.brandId)?.showInfoForceUntil);
		const subscription = computed(() => store.state.general.subscription);

		// --- Computed ---
		const tip = computed(() => {
			return `To see brand names in the Matches Area, switch on 'Visible'. This will also display your own brand name, and once activated, it cannot be turned off for 30 days. To hide your brand name, switch on 'Hidden'. This will also hide the other brand names.`;
		});

		const p = computed(() => {
			return showInfo.value
				? `Other brands can see your brand name and logo in the Matches Area.`
				: `Your brand is hidden and therefore you cannot see other brand names in the Matches area.`;
		});

		// --- Watchers ---
		watch(
			() => props.show_info,
			(newVal) => {
				showInfo.value = newVal;
			}
		);

		watch(showInfo, (newVal) => {
			if (noWatch.value) {
				setTimeout(() => {
					noWatch.value = false;
				}, 500);
				return;
			}

			if (showInfoForceUntil.value) {
				return;
			}

			let msg = newVal
				? `When you make your brand visible, other users will be able to see your brand name and logo, and you'll be able to see theirs. Once turned on, this setting can't be changed for 30 days.`
				: `Are you sure you want to hide your brand? You won't be able to see other brand names in the Matches Area.`;

			confirm(msg, { okText: "OK", cancelText: "Cancel" })
				.then(() => {
					if (!props.brandId) {
						emit("toggle-show-info", showInfo.value);
						return;
					}

					ApiService.post("brand/toggle-show-info/" + props.brandId, { show_info: showInfo.value })
						.then(() => {
							loadDashboard();
							emit("success");
						})
						.catch((err) => {
							noWatch.value = true;
							showInfo.value = !showInfo.value;
							$toast.error(`Error: ${err}`);
						});
				})
				.catch(() => {
					noWatch.value = true;
					showInfo.value = !showInfo.value;
				});
		});

		// --- Lifecycle ---
		onMounted(() => {
			noWatch.value = true;
			if (noWatch.value) {
				setTimeout(() => {
					noWatch.value = props.show_info;
				}, 500);
			}
			showInfo.value = props.show_info;
		});

		return {
			showInfo,
			tip,
			p,
			showInfoForceUntil,
			subscription,
		};
	},
};
</script>
<style lang="less" scoped>
@import "./../assets/css/less/_helper.less";
@import "./../assets/css/less/_settings.less";

.show-info-toggle {
	padding: 0;
	background: @guidedRose;
	padding: 9px 5px 0 5px;
	border-radius: @radiusXs;
	position: relative;
	margin-bottom: 10px;
	overflow: visible;
	h2 {
		margin: 0;
		padding: 0;
		font-size: 14px;
		font-weight: @bold;
		color: white;
		text-align: left;
		margin: 0 0 3px 10px;
	}
	.disabled {
		color: white;
		font-style: italic;
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: fade(@guidedRose, 90%);
		z-index: 4;
		padding: 10px;
		text-align: center;
	}
	> div {
		display: flex !important;
		align-items: flex-start;
		justify-content: flex-start;
		width: 100%;
		.switcher {
			margin: 0;
		}
		p {
			margin: 0;
			padding: 1px 0 10px;
			color: white;
			span {
				font-weight: @bold;
				color: inherit;
			}
		}
	}
}
</style>